import { CompactText } from 'ui/CompactText';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { getMyResponses, type MyResponse } from 'api/v1/requests/bulletins';
import { useBulletinPublic } from 'hooks/useBulletin';
import { useCreateProposalFromTutor } from 'hooks/useCreateProposal';
import { useTutorTrustScore } from 'hooks/useTutorTrustScore';
import { addNotification } from 'notifications';
import {
  formatBulletinCategoryLine,
  formatBulletinGrades,
  formatBulletinStudyMode,
  formatBulletinSubjectTitle,
  formatParticipantLine,
  translatePlannedDuration,
  translateStudentType,
  translateStudyLocations,
  translateTrialPreference,
  translateTutorGender,
  translateTutorResponseLabel,
  formatBulletinSchedule,
} from 'utils/bulletinLabels';
import { resolveTutorChatUrl } from 'utils/openTutorChat';
import { parsePositiveInt } from 'utils/parsePositiveInt';
import { METRIKA_GOALS, reachMetrikaGoal } from 'utils/metrikaGoals';

import styles from './TeacherSearch.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';
import { PageHead } from 'ui/kit/PageHead';

type Step = 'details' | 'compose' | 'limit';

export function tutorRespondStep(input: {
  requested: boolean;
  hasResponse: boolean;
  remaining: number | null;
  blockReason: string;
}): Step {
  if (!input.requested || input.hasResponse) return 'details';
  const limited =
    input.remaining === 0 || /лимит/i.test(input.blockReason);
  return limited ? 'limit' : 'compose';
}

function hourPrice(from?: number | null, to?: number | null): string {
  const fmt = (value: number) => new Intl.NumberFormat('ru-RU').format(value);
  if (from && to) return `${fmt(from)}-${fmt(to)} руб. / час`;
  if (to) return `До ${fmt(to)} руб. / час`;
  if (from) return `От ${fmt(from)} руб. / час`;
  return 'Цена не указана';
}

function Rows({ items }: { items: Array<{ label: string; value: string }> }) {
  if (!items.length) return null;
  return (
    <div className={styles.rows}>
      {items.map((item, index) => (
        <div key={`${item.label}-${index}`} className={styles.row}>
          <p className={styles.rowLabel}>{item.label}</p>
          <p className={styles.rowValue}>{item.value}</p>
        </div>
      ))}
    </div>
  );
}

export const TeacherSearchFiltersItem = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bulletinId = parsePositiveInt(id) ?? 0;
  const { data: bulletin, isLoading } = useBulletinPublic(bulletinId);
  const { mutateAsync: createProposal, isPending } = useCreateProposalFromTutor();
  const { data: trustScore } = useTutorTrustScore();
  const daily = trustScore?.daily_responses;
  const [message, setMessage] = useState('');
  const [price, setPrice] = useState('');
  const [priceError, setPriceError] = useState<string | null>(null);
  const [forcedLimit, setForcedLimit] = useState(false);
  const [sent, setSent] = useState<{
    message: string;
    price: number;
    responseId?: number | null;
    chatId?: number | null;
    roomId?: string | null;
  } | null>(null);

  const requested = searchParams.get('step') === 'respond';
  const hasResponse = !!(bulletin?.my_bulletin_response_id || bulletin?.pair_chat_id);
  const blockReason = bulletin?.tutor_respond_block_reason?.trim() || '';
  const remaining = daily ? daily.responses_remaining : null;
  const step = forcedLimit
    ? 'limit'
    : tutorRespondStep({
        requested,
        hasResponse,
        remaining,
        blockReason,
      });

  const { data: myResponses } = useQuery({
    queryKey: ['my-responses', 'search-limit'],
    queryFn: () => getMyResponses(),
    enabled: step === 'limit',
  });
  const lastResponse: MyResponse | undefined = myResponses?.items?.[0];

  useEffect(() => {
    if (!bulletin) return;
    setPrice((current) => current || String(bulletin.price_from || 1850));
  }, [bulletin]);

  const setStep = (next: 'respond' | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('step', next);
    else params.delete('step');
    setSearchParams(params, { replace: true });
  };

  if (isLoading) {
    return <BrandLoader center label='Загрузка...' />;
  }

  if (!bulletin) {
    return (
      <div className={styles.page}>
        <PageHead crumb='Личный кабинет / Поиск' title='Подробности заявки' />
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Заявка недоступна</h2>
          <p className={styles.panelText}>
            Объявление не найдено, закрыто или у вас нет доступа к этой заявке. Откройте чат из
            списка откликов или вернитесь к поиску.
          </p>
          <button type='button' className={styles.primary} onClick={() => navigate('/teacher/search')}>
            К поиску
          </button>
        </section>
      </div>
    );
  }

  const title = formatBulletinSubjectTitle(bulletin.subject_category);
  const grade = formatBulletinGrades(bulletin);
  const heading = [title, grade].filter(Boolean).join(' - ');
  const place = translateStudyLocations(bulletin.study_locations);
  const trial = translateTrialPreference(bulletin.trial_lesson_preference);
  const rhythm = translatePlannedDuration(bulletin.planned_duration);
  const schedule = formatBulletinSchedule(bulletin);
  const goal = formatBulletinCategoryLine(bulletin.subject_category) || title;
  const studentType = translateStudentType(bulletin.student_type);
  const studentText = [studentType, grade, bulletin.student_name?.trim()].filter(Boolean).join(', ');
  const studyMode = formatBulletinStudyMode(bulletin.self_study);
  const tutorGender = translateTutorGender(bulletin.tutor_gender_preference);
  const responseLabel = translateTutorResponseLabel(bulletin.my_bulletin_response_label_ru);
  const canRespond = bulletin.tutor_can_respond !== false;
  const limitBlock = step === 'limit' || remaining === 0 || /лимит/i.test(blockReason);

  const detailRows = [
    { label: 'Цель', value: goal },
    place ? { label: 'Формат', value: place } : null,
    { label: 'Бюджет', value: hourPrice(bulletin.price_from, bulletin.price_to) },
    rhythm ? { label: 'Ритм', value: rhythm } : null,
    schedule ? { label: 'Удобное время', value: schedule } : null,
    studentText ? { label: 'Ученик', value: studentText } : null,
    studyMode ? { label: 'Кто подает заявку', value: studyMode } : null,
    trial ? { label: 'Пробное занятие', value: trial } : null,
    tutorGender ? { label: 'Пол репетитора', value: tutorGender } : null,
  ].filter((row): row is { label: string; value: string } => Boolean(row));

  const goToChat = async (source?: {
    bulletinId?: number;
    my_bulletin_response_id?: number | null;
    pair_chat_id?: number | null;
    pair_room_id?: string | null;
  }) => {
    try {
      const url = await resolveTutorChatUrl({
        id: source?.bulletinId ?? bulletin.id,
        id_user: bulletin.id_user,
        my_bulletin_response_id:
          source?.my_bulletin_response_id ?? bulletin.my_bulletin_response_id,
        pair_chat_id: source?.pair_chat_id ?? bulletin.pair_chat_id,
        pair_room_id: source?.pair_room_id ?? bulletin.pair_room_id,
      });
      navigate(url);
    } catch (error) {
      console.error('Ошибка открытия чата:', error);
      navigate('/teacher/chat');
    }
  };

  const submit = async () => {
    if (price.trim() && Number(price) <= 0) {
      setPriceError('Укажите цену больше нуля или оставьте поле пустым');
      return;
    }
    setPriceError(null);
    const proposed = Number(price) || bulletin.price_from || 1850;
    try {
      const response = await createProposal({
        data: {
          bulletin_id: bulletinId,
          message,
          proposed_price: proposed,
        },
        skipNavigate: true,
      });
      reachMetrikaGoal(METRIKA_GOALS.taskResponded);
      setSent({
        message,
        price: proposed,
        responseId: response?.id ?? bulletin.my_bulletin_response_id,
        chatId: response?.chat_id ?? bulletin.pair_chat_id,
        roomId: response?.room_id ?? bulletin.pair_room_id,
      });
    } catch (error) {
      let msg = blockReason || 'Не удалось отправить отклик';
      if (isAxiosError(error)) {
        const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
        if (typeof detail === 'string' && detail.trim()) msg = detail;
      }
      if (/лимит/i.test(msg)) {
        setForcedLimit(true);
        return;
      }
      addNotification(msg, 'error');
      console.error('Ошибка при отклике:', error);
    }
  };

  const shell = (pageTitle: string, crumb: string, body: ReactNode) => (
    <div className={styles.page}>
      <PageHead crumb={crumb} title={pageTitle} />
      <div className={styles.stack}>{body}</div>
    </div>
  );

  if (sent) {
    return shell(
      'Отклик отправлен',
      'Личный кабинет / Чаты и отклики',
      <>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Сообщение отправлено</h2>
          <p className={styles.panelText}>
            Отклик ушел ученику. Дальше можно согласовать время и цену в чате.
          </p>
          <Rows
            items={[
              { label: 'Заявка', value: heading },
              place ? { label: 'Формат', value: place } : null,
              { label: 'Предложенная цена', value: `${new Intl.NumberFormat('ru-RU').format(sent.price)} руб. / час` },
              trial ? { label: 'Пробное занятие', value: trial } : null,
            ].filter((row): row is { label: string; value: string } => Boolean(row))}
          />
        </section>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Ваше сообщение</h2>
          <p className={styles.panelText}>
            {sent.message.trim() || 'Сообщение без текста. Условия можно дописать в чате.'}
          </p>
          <div className={styles.note}>
            <p className={styles.noteTitle}>Следующий шаг</p>
            <CompactText className={styles.noteText} tone={'info'}>
              Откройте чат, чтобы согласовать время, темп и цену. Ответ ученика можно подождать спокойно.
            </CompactText>
          </div>
          <button
            type='button'
            className={styles.primary}
            onClick={() =>
              void goToChat({
                my_bulletin_response_id: sent.responseId,
                pair_chat_id: sent.chatId,
                pair_room_id: sent.roomId,
              })
            }
          >
            Открыть чат
          </button>
        </section>
      </>,
    );
  }

  if (step === 'limit') {
    return shell(
      'Лимит откликов достигнут',
      'Личный кабинет / Чаты и отклики',
      <>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Новый отклик сейчас недоступен</h2>
          <p className={styles.panelText}>
            {blockReason || 'Новые отклики сейчас недоступны по лимиту. Уже отправленные можно открыть.'}
          </p>
          <Rows
            items={[
              {
                label: 'Новые отклики',
                value: daily
                  ? `${daily.responses_used} из ${daily.responses_max}`
                  : 'Недоступны по лимиту',
              },
              { label: 'Отправленные отклики', value: 'Можно просмотреть' },
            ]}
          />
          <div className={styles.note}>
            <p className={styles.noteTitle}>Следующий шаг</p>
            <CompactText className={styles.noteText} tone={'info'}>
              Посмотрите отправленные сообщения и продолжите доступные диалоги. Условия занятия
              согласуйте там.
            </CompactText>
          </div>
          <button type='button' className={styles.primary} onClick={() => navigate('/teacher/chat')}>
            Смотреть отправленные отклики
          </button>
        </section>
        {lastResponse ? (
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>{lastResponse.bulletin_title || 'Последний отклик'}</h2>
            <Rows
              items={[
                {
                  label: lastResponse.bulletin_title || 'Заявка',
                  value: 'Сообщение отправлено',
                },
              ]}
            />
            <p className={styles.panelText}>
              {lastResponse.proposed_price
                ? `${new Intl.NumberFormat('ru-RU').format(lastResponse.proposed_price)} руб. / час.`
                : 'Цена в этом отклике не указана.'}{' '}
              {lastResponse.message?.trim() || 'Время и условия еще можно согласовать в чате.'}
            </p>
            <button
              type='button'
              className={styles.secondary}
              onClick={() =>
                void goToChat({
                  bulletinId: lastResponse.bulletin_id,
                  my_bulletin_response_id: lastResponse.id,
                })
              }
            >
              Открыть доступный диалог
            </button>
          </section>
        ) : null}
      </>,
    );
  }

  if (step === 'compose') {
    return shell(
      'Отклик на заявку',
      'Личный кабинет / Поиск',
      <>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Ваш ответ ученику</h2>
          <p className={styles.panelText}>
            {heading}. {goal !== heading ? goal : 'Напишите, как пройдет первое занятие.'}
          </p>
          <div className={styles.form}>
            <label className={styles.control}>
              <span className={styles.fieldLabel}>Сообщение</span>
              <textarea
                className={styles.textarea}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder='Коротко: чем можете помочь и как пройдет первое занятие'
              />
            </label>
            <label className={styles.control}>
              <span className={styles.fieldLabel}>Цена часа, руб.</span>
              <input
                className={styles.input}
                inputMode='numeric'
                value={price}
                onChange={(event) => {
                  setPrice(event.target.value.replace(/\D/g, ''));
                  setPriceError(null);
                }}
                aria-invalid={priceError ? true : undefined}
              />
              {priceError ? (
                <span className='fig-field-error' role='alert'>
                  {priceError}
                </span>
              ) : null}
            </label>
          </div>
        </section>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Перед отправкой</h2>
          <div className={styles.note}>
            <p className={styles.noteTitle}>Отклик открывает чат</p>
            <CompactText className={styles.noteText} tone={'info'}>
              Не обещайте конкретный балл. Формат, цену и оплату занятия согласуйте в чате.
            </CompactText>
          </div>
          <div className={styles.actions}>
            <button type='button' className={styles.primary} disabled={isPending} onClick={() => void submit()}>
              {isPending ? 'Отправляем...' : 'Отправить отклик'}
            </button>
            <button type='button' className={styles.secondary} onClick={() => setStep(null)}>
              Отмена
            </button>
          </div>
        </section>
      </>,
    );
  }

  return shell(
    'Подробности заявки',
    'Личный кабинет / Поиск',
    <>
      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{heading}</h2>
        <Rows items={detailRows} />
        {(bulletin.participants || []).length > 0 && !bulletin.self_study ? (
          <Rows
            items={(bulletin.participants || []).map((child, index) => ({
              label: index === 0 ? 'Дети в заявке' : 'Еще',
              value: formatParticipantLine(child),
            }))}
          />
        ) : null}
      </section>
      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Пожелания к занятиям</h2>
        <p className={styles.panelText}>
          {bulletin.wishes?.trim() || 'Пожелания к занятиям ученик пока не написал.'}
        </p>
        <div className={styles.note}>
          <p className={styles.noteTitle}>Проверьте совместимость</p>
          <CompactText className={styles.noteText} tone={'info'}>
            Откликайтесь, если вы можете взять эту цель и формат. Цену часа и время подтвердите в
            сообщении.
          </CompactText>
        </div>
        {responseLabel ? <CompactText className={styles.hint} tone={'info'}>{responseLabel}</CompactText> : null}
        {!hasResponse && blockReason && !limitBlock ? <CompactText className={styles.hint} tone={'info'}>{blockReason}</CompactText> : null}
        <div className={styles.actions}>
          <button
            type='button'
            className={styles.primary}
            disabled={isPending || (!hasResponse && !canRespond && !limitBlock)}
            onClick={() => {
              if (hasResponse) {
                void goToChat();
                return;
              }
              setStep('respond');
            }}
          >
            {hasResponse ? 'Перейти к чату' : 'Откликнуться'}
          </button>
          <button type='button' className={styles.secondary} onClick={() => navigate('/teacher/search')}>
            К поиску
          </button>
        </div>
      </section>
    </>,
  );
};
